import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import type { Badge } from '../LibraryDeps'
import { BadgeChips } from './BadgeChips'
import { BadgeMenu, type BadgeState } from './BadgeMenu'
import type { BadgeCatalog } from './badges'

export interface ItemBadges {
  catalog: BadgeCatalog
  /** The shown item's badges, in catalog order. */
  badges: Badge[]
  stateOf: (badge: Badge) => BadgeState
  /** Absent = read-only (no Vault write): chips only. */
  onToggle?: (badge: Badge, on: boolean) => void
  /** Bumped by the viewer's B shortcut: open the menu. */
  openSignal?: number
}

/** The viewer's Badges: the item's chips and a 🏷 that opens the dropdown (in the Operator note card's header). */
export function BadgeControl({ catalog, badges, stateOf, onToggle, openSignal = 0 }: ItemBadges) {
  const { t } = useTranslation('library')
  const [open, setOpen] = useState(false)
  const seen = useRef(openSignal)
  useEffect(() => {
    if (openSignal === seen.current) return
    seen.current = openSignal
    if (onToggle) setOpen(true)
  }, [openSignal, onToggle])

  if (!onToggle && !badges.length) return null
  return (
    <span className="lib-badge-ctl">
      <BadgeChips badges={badges} />
      {onToggle && (
        <span className="lib-anchor lib-anchor--start">
          <button
            type="button"
            className={`lib-badge-add ${badges.length ? '' : 'is-empty'}`}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={t('vault.badges.label')}
            title={t('vault.badges.hint')}
            onClick={() => setOpen((o) => !o)}
          >
            <FeatherIcon name="tag" size={12} />
            {badges.length ? <FeatherIcon name="plus" size={11} /> : t('vault.badges.add')}
          </button>
          {open && <BadgeMenu catalog={catalog} stateOf={stateOf} onToggle={onToggle} canManage onClose={() => setOpen(false)} />}
        </span>
      )}
    </span>
  )
}
